import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  MOTIF_MODAL_DATA,
  MotifButtonModule,
  MotifCardModule,
  MotifFormsModule,
  MotifMenuModule,
  MotifModalModule,
  MotifModalRef,
} from '@ey-xd/ng-motif';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ApplicationConstants,
  CommonConstants,
  DeliverablesConstants,
} from '@sa/app/shared/constants/ApplicationConstants';
import { IApplicationAgent } from '@sa/app/shared/interfaces/ApplicationAgent';
import { ApplicationUser } from '@sa/app/shared/interfaces/ApplicationUser';
import { IAssigneeDropdown } from '@sa/app/shared/interfaces/AssigneeDropdown';
import { WorkspaceTemplateItemRequest } from '@sa/app/shared/interfaces/WorkspaceTemplateItemCreateRequest';
import { WorkspaceTemplateItemDetailsResponse } from '@sa/app/shared/interfaces/WorkspaceTemplateItemDetailsResponse';
import { AgentService } from '@sa/app/shared/services/agent-service';
import { CommonService } from '@sa/app/shared/services/common.service';
import { SessionStorageService } from '@sa/app/shared/services/session-storage.service';
import { UserAgentService } from '@sa/app/shared/services/user-agent.service';
import { WorkspaceTemplateService } from '@sa/app/shared/services/workspace-template.service';
import { LoggerService } from '@sa/core/services/logger.service';
import { WorkspaceService } from '@sa/shared/services/workspace.service';
import { DueDateOptionResponse } from '@sa/shared/interfaces/DueDateOptionResponse';
import { GTPwTaskOptionResponse } from '@sa/shared/interfaces/GTPwTaskOptionResponse';

@Component({
  selector: 'app-add-task-template-panel',
  standalone: true,
  imports: [
    CommonModule,
    MotifMenuModule,
    FormsModule,
    MotifCardModule,
    MotifFormsModule,
    MotifButtonModule,
    MotifModalModule,
  ],
  templateUrl: './add-task-template-panel.component.html',
  styleUrls: ['./add-task-template-panel.component.scss'],
})
export class AddTaskTemplatePanelComponent {
  @Output() public visibleChange: WritableSignal<boolean> = signal(false);

  protected dropdownOptions: IAssigneeDropdown[] = [];
  protected milestoneName?: string = '';
  protected toggleValue: boolean = true; //true = Agent, false = User
  protected taskName: string = '';
  protected prompt: string = '';
  protected dueDateOptions: DueDateOptionResponse[] = [];
  protected selectedDueDateOption: DueDateOptionResponse | null = null;
  protected readonly NULL_GTPW_OPTION: GTPwTaskOptionResponse = {
    id: -1,
    label: 'Select',
    value: -1,
    isAssociated: false,
  };
  protected gtpwTaskOptions: GTPwTaskOptionResponse[] = [];
  protected selectedGTPwTaskOption: GTPwTaskOptionResponse =
    this.NULL_GTPW_OPTION;
  protected fileUploadEnabled: boolean = true;
  protected modalData = inject(MOTIF_MODAL_DATA);
  commonService = inject(CommonService);
  protected AddTaskConstants = DeliverablesConstants.AddTaskPanel;
  protected action: any;
  protected parentId: any;
  protected assignedToVisiblity: boolean = true;
  private workspaceTemplateItemGuid: any = null;
  private specialCharPattern = /[^a-zA-Z0-9./_\\-]/;
  private selectedTenantGuid: string = '';
  private selectedAssigneeId: string = '';
  protected taskWorkspaceTemplateItemDetails!: WorkspaceTemplateItemDetailsResponse;
  private workspaceTemplateService = inject(WorkspaceTemplateService);
  workspaceTemplates$ = this.workspaceTemplateService.workspaceTemplates$;

  private readonly _workspaceService: WorkspaceService =
    inject(WorkspaceService);
  private readonly _loggerService: LoggerService = inject(LoggerService);
  private readonly _cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly _userAgentService: UserAgentService =
    inject(UserAgentService);
  private readonly _sessionStorageService: SessionStorageService = inject(
    SessionStorageService
  );
  private readonly agentService: AgentService = inject(AgentService);
  private modalRef = inject(MotifModalRef);
  private readonly _destroyRef: DestroyRef = inject(DestroyRef);
  workspaceTemplateGuid: any;
  workspaceTemplateName: string = '';

  ngOnInit() {
    this.action = this.modalData.action;
    this.taskWorkspaceTemplateItemDetails =
      this.modalData.workspaceTemplateItemDetails;
    this.selectedTenantGuid =
      this._sessionStorageService.getItem<string>(
        ApplicationConstants.SessionKeyConstants.TenantId
      ) ?? '';
    const selectedTemplate =
      this.workspaceTemplateService.getSelectedTemplate();

    if (selectedTemplate) {
      this.workspaceTemplateGuid = selectedTemplate.workspaceTemplateGuid;
      this.workspaceTemplateName = selectedTemplate.workSpaceTemplateName;
    }
    this.milestoneName = this.modalData.milestoneName;
    // Assign before loadDropDownOptions() so the GTPw task query is scoped to this item, not the whole template.
    if (this.action == 'Edit') {
      this.workspaceTemplateItemGuid = this.modalData.workspaceTemplateItemGuid;
    }
    this.loadUserDropdown();
    this.loadDropDownOptions();
    if (this.action == 'Edit') {
      this.getMilestone();
      this.taskName = this.taskWorkspaceTemplateItemDetails.title;
      const uploadFlag = (this.taskWorkspaceTemplateItemDetails as any)
        ?.fileUploadEnabled;
      this.fileUploadEnabled =
        typeof uploadFlag === 'boolean'
          ? uploadFlag
          : typeof uploadFlag === 'string'
            ? uploadFlag.trim().toLowerCase() === 'true'
            : this.fileUploadEnabled;
      this.prompt = this.taskWorkspaceTemplateItemDetails.description;
      this.parentId = this.taskWorkspaceTemplateItemDetails.parentId;
      this.toggleValue = this.getToggleValue(
        this.taskWorkspaceTemplateItemDetails
      );
      this.taskWorkspaceTemplateItemDetails.typeId ===
        DeliverablesConstants.WorkspaceItemType.Tasks.id &&
      !this.taskWorkspaceTemplateItemDetails.assignedToUser &&
      !this.taskWorkspaceTemplateItemDetails.assignedToAgent
        ? false
        : (!this.taskWorkspaceTemplateItemDetails.assignedToUser &&
              !this.taskWorkspaceTemplateItemDetails.assignedToAgent) ||
            !!this.taskWorkspaceTemplateItemDetails.assignedToAgent?.trim()
          ? true
          : false;
      this.selectedAssigneeId =
        this.taskWorkspaceTemplateItemDetails.assignedToAgent?.trim()
          ? this.taskWorkspaceTemplateItemDetails.assignedToAgent
          : this.taskWorkspaceTemplateItemDetails.assignedToUser;
    }
  }

  private _selectedAssignee: IAssigneeDropdown | null = null;
  get selectedAssignee(): IAssigneeDropdown | null {
    return this._selectedAssignee;
  }
  set selectedAssignee(value: IAssigneeDropdown | null) {
    this._selectedAssignee = value;
  }
  protected compareAssignee = (
    a: IAssigneeDropdown | null,
    b: IAssigneeDropdown | null
  ): boolean => a?.label === b?.label;
  protected onAssigneeToSelection(event: IAssigneeDropdown): void {
    this.selectedAssignee = event;
    this.selectedAssigneeId = event.label;
  }
  protected onDueDateSelection(event: DueDateOptionResponse): void {
    this.selectedDueDateOption = event;
  }

  protected onGTPwTaskSelection(event: GTPwTaskOptionResponse): void {
    this.selectedGTPwTaskOption = event ?? this.NULL_GTPW_OPTION;
  }
  protected onCreate(): void {
    const Message =
      this.action === 'Edit'
        ? DeliverablesConstants.AddTaskPanel.Message.EditSuccess
        : DeliverablesConstants.AddTaskPanel.Message.AddSuccess;

    if (!this.taskName || this.taskName.trim().length === 0) {
      this._loggerService.logError('Task name is required.');
      return;
    }

    const taskItem: WorkspaceTemplateItemRequest = {
      workspaceTemplateGuid: this.workspaceTemplateGuid,
      typeId: DeliverablesConstants.WorkspaceItemType.Tasks.id,
      title: this.taskName?.trim() ?? '',
      description: this.prompt ?? '',
      isActive: true,
      parentGuid: this.modalData.parentGuid ?? null,
      workspaceTemplateItemGuid:
        this.action === 'Edit'
          ? (this.modalData.workspaceTemplateItemGuid ?? null)
          : null,
      assignedTo: '',
      assignedToAgent:
        this.toggleValue && this.selectedAssigneeId?.trim()
          ? this.selectedAssigneeId
          : null,
      assignedToUser:
        !this.toggleValue && this.selectedAssigneeId?.trim()
          ? this.selectedAssigneeId
          : null,
      fileUploadEnabled: this.fileUploadEnabled,
      dueDateType: this.selectedDueDateOption?.value ?? null,
      gtPwTaskId:
        this.selectedGTPwTaskOption && this.selectedGTPwTaskOption.value > 0
          ? this.selectedGTPwTaskOption.value
          : null,
    };
    this._workspaceService
      .createWorkspaceTemplateItem(this.selectedTenantGuid ?? null, taskItem)
      .subscribe({
        next: response => {
          this._loggerService.logSuccess(Message);
          this.modalRef.close(true); // Closes the modal
        },
        error: err => {
          this._loggerService.logApiError(
            err?.error?.message ||
              DeliverablesConstants.AddTaskPanel.Message.ErrorInCreation
          );
        },
      });
  }

  get isTaskNameInvalid(): boolean {
    return this.specialCharPattern.test(this.taskName || '');
  }

  // #region PRIVATE METHODS

  private loadDropDownOptions(): void {
    this._workspaceService
      .getDueDateOptions(this.selectedTenantGuid)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (options: DueDateOptionResponse[]) => {
          this.dueDateOptions = options || [];
          // Preselect option if editing and value present (guarded via any to avoid type issues)
          if (this.action === 'Edit') {
            const type = (this.taskWorkspaceTemplateItemDetails as any)
              ?.dueDateOptionID;
            if (type) {
              const match = this.dueDateOptions.find(
                (o: DueDateOptionResponse) => o.value === type
              );
              this.selectedDueDateOption = match || null;
            }
          }
          this._cdr.detectChanges();
        },
        error: (err: any) => {
          this.dueDateOptions = [];
        },
      });

    // Templates are edited at the client level and have no live workspace instance,
    // so use the workspace template GUID as the scope for GTPw tasks instead of
    // sessionStorage's 'workspace' entry (which is only populated after visiting an
    // actual workspace/deliverable page and is unrelated to this template).
    if (!this.workspaceTemplateGuid || !this.selectedTenantGuid) {
      this._loggerService.logError(
        'Cannot load GTPw tasks: missing workspace or tenant context.'
      );
      return;
    }

    const workspaceItemGuid =
      this.workspaceTemplateItemGuid &&
      this.workspaceTemplateItemGuid.trim() !== ''
        ? this.workspaceTemplateItemGuid
        : undefined;

    this._workspaceService
      .getGTPwTasksForTemplate(
        this.selectedTenantGuid,
        this.workspaceTemplateGuid,
        workspaceItemGuid
      )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (options: GTPwTaskOptionResponse[]) => {
          this.gtpwTaskOptions = [this.NULL_GTPW_OPTION, ...options];
          if (this.action === 'Edit') {
            const existingId =
              (this.taskWorkspaceTemplateItemDetails as any)?.gtPwTaskID ??
              (this.taskWorkspaceTemplateItemDetails as any)?.GTPwTaskID ??
              null;
            if (existingId !== null && existingId !== undefined) {
              const match = this.gtpwTaskOptions.find(
                g => g.value === Number(existingId)
              );
              this.selectedGTPwTaskOption = match ?? this.NULL_GTPW_OPTION;
            }
          }
          this._cdr.detectChanges();
        },
        error: err => {
          console.error('Failed to load GTPw tasks', err);
          this._loggerService.logError('Failed to load load GTPw tasks');
        },
      });
  }

  private getToggleValue(
    templateDetails: WorkspaceTemplateItemDetailsResponse
  ): boolean {
    const details = templateDetails;
    if (
      details.typeId === DeliverablesConstants.WorkspaceItemType.Tasks.id &&
      details.title.trim().toLowerCase().includes('human')
    ) {
      if (!details.assignedToUser && !details.assignedToAgent) return false;
      else return true;
    } else if (!details.assignedToUser && !details.assignedToAgent) return true;
    else if (details.assignedToAgent && details.assignedToAgent.trim())
      return true;
    else return false;
  }

  private loadUserDropdown() {
    if (this.toggleValue) this.getAgents();
    else this.dropdownOptions = [];
    //this.getApplicationUsers();
  }

  private getApplicationUsers() {
    this._userAgentService
      .getApplicationUsers(this.selectedTenantGuid)
      .subscribe((data: ApplicationUser[]) => {
        this.dropdownOptions = data
          .filter(item => item.isActive)
          .map(item => ({
            label: item.userGuid,
            value: [item.firstName, item.lastName].filter(Boolean).join(' '),
          }));
        if (
          this.action === 'Edit' &&
          !this.toggleValue &&
          this.taskWorkspaceTemplateItemDetails.assignedToUser !== null &&
          this.taskWorkspaceTemplateItemDetails.assignedToUser !== undefined
        ) {
          this.selectedAssignee =
            this.dropdownOptions.find(
              option =>
                option.label ===
                this.taskWorkspaceTemplateItemDetails.assignedToUser
            ) || null;
        }
      });
  }

  onToggleSelection(event: any) {
    this.dropdownOptions = [];
    this.loadUserDropdown();
  }

  private getAgents() {
    this._userAgentService.getAgents().subscribe({
      next: (data: IApplicationAgent[]) => {
        this.dropdownOptions = data.map(item => ({
          label: item.applicationAgentGuid,
          value: item.agentName,
        }));
        this.SelectAgent();
      },
    });
  }

  SelectAgent() {
    if (
      this.action === 'Edit' &&
      this.toggleValue &&
      this.taskWorkspaceTemplateItemDetails.assignedToAgent !== null &&
      this.taskWorkspaceTemplateItemDetails.assignedToAgent !== undefined
    ) {
      this.selectedAssignee =
        this.dropdownOptions.find(
          option =>
            option.label ===
            this.taskWorkspaceTemplateItemDetails.assignedToAgent
        ) || null;
    }
  }

  getMilestone() {
    this._workspaceService
      .loadWorkspaceTemplateItem(
        this.selectedTenantGuid,
        this.workspaceTemplateGuid
      )
      .subscribe({
        next: response => {
          if (!response?.children || !Array.isArray(response.children)) {
            console.warn('No children found in response');
            return;
          }
          // Use loose comparison (==) to handle string/number mismatch
          const milestone = response.children.find(
            child =>
              child.data?.type === 'Milestones' &&
              child.data?.workspaceTemplateItemId == this.parentId &&
              child.data?.title
          );

          this.milestoneName = milestone?.data?.title || '';

          if (!this.milestoneName) {
            console.warn(`No milestone found for parentId: ${this.parentId}`);
          }
        },
        error: err => {
          console.error('Error loading milestone:', err);
          this._loggerService.logError('Failed to load milestone information');
        },
      });
  }
}

// #endregion
